/**
 * Manage assignments component.
 *
 * @author Ryan Sweeney <rsweeney@buildcentrix.com>
 * @copyright (c) 2025 Build Centrix
 */
import { Component, Inject, ViewEncapsulation } from '@angular/core';
import { IVehicleAssignmentModel } from '../../../interfaces/vehicles/vehicle-interfaces';
import angular from 'angular';
import { WdVehicleAssignmentService } from '../../../services/vehicles/assignments/wd-vehicle-assignment.service';
import { IBcxErrorService } from '../../../interfaces/generic/generic-interfaces';
import { WdLocalSharedFunctionsService } from '../../../services/shared/wd-local-shared-functions.service';
import { WdLocalEnvironmentService } from '../../../services/environment/wd-local-environment.service';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';

@Component({
  selector: 'app-popover-assignments',
  templateUrl: './order-assignments.component.html',
  styleUrls: ['./order-assignments.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class OrderAssignmentsComponent {
  // Strings
  orderId: string;
  orderNumber: string;

  // Assignments
  assignments: Array<IVehicleAssignmentModel> = [];
  assignmentsUntouched: Array<IVehicleAssignmentModel> = [];

  // Assignment Options
  assignmentOptions: Array<IVehicleAssignmentModel> = [];

  // Booleans
  isUpdating: boolean;

  /**
   * Constructor
   */
  constructor(
    public wdLocalEnvironmentService: WdLocalEnvironmentService,
    public wdLocalSharedFunctionsService: WdLocalSharedFunctionsService,
    public wdVehicleAssignmentService: WdVehicleAssignmentService,
    @Inject('wdErrorService') public wdErrorService: IBcxErrorService,
    public dialogRef: MatDialogRef<OrderAssignmentsComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any,
  ) {
    this.orderId = data.orderId;
    this.orderNumber = data.orderNumber;
    this.assignments = data.assignments;
    this.assignmentsUntouched = angular.copy(data.assignments);
    this.assignmentOptions = data.assignmentOptions;
  }

  /**
   * Close the dialog returning a result.
   */
  closeModal(result: boolean) {
    this.dialogRef.close(result);
  }

  /**
   * Update assigned vehicles for orders.
   */
  async updateAssignedVehicles() {
    this.isUpdating = true;
    try {
      // Get selected assignments and options
      const orderId = this.orderId;
      const assignmentIds = this.assignments.map(
        (assignment: IVehicleAssignmentModel) => assignment.id,
      );
      const assignmentOptions = this.assignmentOptions;
      // Build lists of vehicle assignments we need to update
      const addToAssignments = [];
      const removeFromAssignments = [];
      assignmentOptions.forEach((assignment: IVehicleAssignmentModel) => {
        if (assignmentIds.includes(assignment.id)) {
          // Order has been added to vehicle
          addToAssignments.push(assignment.id);
        } else {
          // Order has been removed from vehicle
          removeFromAssignments.push(assignment.id);
        }
      });
      // Add or remove order from vehicle assignments
      await this.wdVehicleAssignmentService.updateVehicleAssignmentOrders({
        orderId,
        addToAssignments,
        removeFromAssignments,
      });
    } catch (error) {
      // Error
      this.wdErrorService.userError('Error', error.statusText);
    } finally {
      // Close the dialog, return false
      this.closeModal(true);
      // Stop loader
      this.isUpdating = false;
    }
  }
}
